
"use client";

import Link from 'next/link';
import { useState, useEffect } from 'react';

export default function DocsPage() {
    const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
    const [activeSection, setActiveSection] = useState<string | null>(null);
    const [isTocVisible, setIsTocVisible] = useState(false);


    // Toggle collapsible sections
    const toggleSection = (sectionId: string) => {
        setOpenSections((prev: Record<string, boolean>) => ({
            ...prev,
            [sectionId]: !prev[sectionId],
        }));
    };

    // Smooth scroll to section
    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setActiveSection(sectionId);
        }
    };

    const toggleToc = () => {
        setIsTocVisible((prev) => !prev);
    };

    // Handle scroll events to update active section
    useEffect(() => {
        const handleScroll = () => {
            const sections = toc.map(item => item.id);
            for (const section of sections) {
                const element = document.getElementById(section);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    if (rect.top <= 100 && rect.bottom >= 100) {
                        setActiveSection(section);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Table of contents data
    const toc = [
        { id: 'prerequisites', title: '1. Prerequisites', level: 0 },
        { id: 'tech-stack', title: '2. Tech Stack Overview', level: 0 },
        { id: 'getting-started', title: '3. Getting Started', level: 0 },
        { id: 'installation', title: '3.1 Installation', level: 1 },
        { id: 'customize-brand', title: '3.2 Customize Your Brand', level: 1 },
        { id: 'protected-routes', title: '3.3 Protected Routes', level: 1 },
        { id: 'authentication', title: '4. Authentication System', level: 0 },
        { id: 'google-oauth', title: '4.1 Configure Google OAuth', level: 1 },
        { id: 'roles-users', title: '4.2 Roles and User Types', level: 1 },
        { id: 'extend-user', title: '4.3 Extend User Model', level: 1 },
        { id: 'database-orm', title: '5. Database and ORM', level: 0 },
        { id: 'folder-structure', title: '6. Folder Structure', level: 0 },
        { id: 'listings-reviews', title: '7. Listings and Reviews', level: 0 },
        { id: 'integrations', title: '8. Integrations', level: 0 },
        { id: 'stripe', title: '8.1 Stripe (Subscriptions)', level: 1 },
        { id: 'resend', title: '8.2 Emails with Resend', level: 1 },
        { id: 'maps', title: '8.3 Maps (Mapbox + OpenStreetMap)', level: 1 },
        { id: 'currency', title: '8.4 FreeCurrencyAPI', level: 1 },
        { id: 'recaptcha', title: '8.5 Google reCAPTCHA', level: 1 },
        { id: 'newsletter', title: '8.6 Newsletter Footer', level: 1 },
        { id: 'cron-jobs', title: '9. Cron Jobs', level: 0 },
        { id: 'seo', title: '10. SEO & Programmatic SEO', level: 0 },
        { id: 'chatgpt-prompts', title: '11. ChatGPT Prompts', level: 0 },
        { id: 'deployment', title: '12. Deployment', level: 0 },
        { id: 'vercel-supabase', title: '12.1 Vercel + Supabase', level: 1 },
        { id: 'coolify-vps', title: '12.2 Coolify + VPS', level: 1 },
        { id: 'troubleshooting', title: '13. Troubleshooting', level: 0 },
        { id: 'reusing-peerquik', title: '14. Reusing PeerQuik', level: 0 },
        { id: 'features', title: '15. Features at a Glance', level: 0 },
    ];


    return (
        <div className="min-h-screen bg-gray-900 text-gray-100 flex flex-col">
            {/* Header */}
            <header className="bg-gray-800 shadow-md">
                <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
                    <h1 className="text-3xl font-bold text-white">PeerQuik Documentation</h1>
                    <p className="mt-1 text-sm text-gray-300">The Ultimate SaaS Starter Kit Installation Guide</p>
                </div>
            </header>

            {/* Main Content */}
            <div className="sm:flex sm:flex-row py-8 px-6 sm:gap-8">
                <aside className="sm:w-1/4 bg-gray-800 p-6 rounded-lg shadow-md sticky top-8 self-start max-h-[calc(100vh-100px)] overflow-y-auto scrollbar-thin">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-white">Table of Contents</h3>
                        <button
                            onClick={toggleToc}
                            className="sm:hidden text-cyan-400 hover:text-cyan-300 focus:outline-none"
                        >
                            {isTocVisible ? '▼' : '►'}
                        </button>
                    </div>
                    <ul className={`space-y-3 ${isTocVisible ? 'block' : 'hidden'} sm:block`}>
                        {toc.map((item) => (
                            <li key={item.id} className={`${item.level === 1 ? 'pl-4' : ''}`}>
                                <button
                                    onClick={() => scrollToSection(item.id)}
                                    className={`text-left w-full py-1 px-2 rounded transition-colors duration-200 ${activeSection === item.id
                                        ? 'bg-cyan-700 text-white font-medium'
                                        : 'text-gray-300 hover:text-cyan-300 hover:bg-gray-700'
                                        }`}
                                >
                                    {item.title}
                                </button>
                            </li>
                        ))}
                    </ul>
                </aside>

                {/* Documentation Content */}
                <div className="w-full sm:w-3/4 bg-gray-800 p-8 rounded-lg shadow-md">
                    <h2 className="text-2xl font-semibold mb-6 text-white">PeerQuik: The Ultimate SaaS Starter Kit</h2>
                    <p className="mb-6">
                        PeerQuik is a production-ready Next.js 14 boilerplate designed to jumpstart your B2C or C2C application, whether it’s a marketplace, SaaS with subscriptions, or a custom portal. It includes prebuilt features like authentication, Stripe payments, an admin panel, reviews, emails, cron jobs, maps, currency conversion, and more—saving you hours of development time.
                    </p>
                    <p className="mb-6">
                        This guide is beginner-friendly and assumes no prior experience with some tools. You can reuse PeerQuik for multiple projects by repeating the setup process for each new instance. Just copy the project folder and follow the steps below.
                    </p>

                    {/* Section 1: Prerequisites */}
                    <div id="prerequisites" className="mb-10 border-b border-gray-700 pb-6">
                        <h3
                            className="text-xl font-bold mb-4 cursor-pointer text-cyan-400 hover:text-cyan-300 flex items-center"
                            onClick={() => toggleSection('prerequisites')}
                        >
                            1. 📋 Prerequisites
                            <span className="ml-2">{openSections['prerequisites'] !== false ? '▲' : '▼'}</span>
                        </h3>
                        {openSections['prerequisites'] !== false && (
                            <div className="pl-4 text-gray-200 space-y-4">
                                <p>Before you start, ensure you have the following installed:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><strong>Node.js</strong> (v18 or higher): Download from <Link href="https://nodejs.org" className="text-cyan-400 hover:text-cyan-300">nodejs.org</Link>.</li>
                                    <li><strong>npm</strong>: Comes with Node.js. Verify with <code className="bg-gray-700 px-1 rounded">npm -v</code>.</li>
                                    <li><strong>Code Editor</strong>: We recommend <Link href="https://code.visualstudio.com" className="text-cyan-400 hover:text-cyan-300">Visual Studio Code</Link>.</li>
                                    <li><strong>PostgreSQL</strong> (local) or <strong>Supabase</strong> (cloud): For the database.</li>
                                    <li><strong>Docker</strong> (optional): For VPS deployment with Coolify.</li>
                                    <li><strong>Accounts</strong>:
                                        <ul className="list-circle pl-6 space-y-1">
                                            <li>Google Cloud Console (for OAuth).</li>
                                            <li>Stripe (for payments).</li>
                                            <li>Resend (for emails).</li>
                                            <li>Mapbox (for maps).</li>
                                            <li>FreeCurrencyAPI (for currency conversion).</li>
                                            <li>Google reCAPTCHA (for form protection).</li>
                                        </ul>
                                    </li>
                                </ul>
                                <p className="mt-4"><strong>Note</strong>: Sign up for these services in advance, as you’ll need API keys during setup.</p>
                            </div>
                        )}
                    </div>

                    {/* Section 2: Tech Stack Overview */}
                    <div id="tech-stack" className="mb-10 border-b border-gray-700 pb-6">
                        <h3
                            className="text-xl font-bold mb-4 cursor-pointer text-cyan-400 hover:text-cyan-300 flex items-center"
                            onClick={() => toggleSection('tech-stack')}
                        >
                            2. 🔧 Tech Stack Overview
                            <span className="ml-2">{openSections['tech-stack'] !== false ? '▲' : '▼'}</span>
                        </h3>
                        {openSections['tech-stack'] !== false && (
                            <div className="pl-4 text-gray-200 space-y-4">
                                <p className="mb-4">PeerQuik uses modern, industry-standard technologies:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><strong>Next.js 14 (App Router)</strong>: A React framework for server-side rendering (SSR) and static site generation (SSG). The App Router organizes routes by folders.</li>
                                    <li><strong>TypeScript</strong>: Adds type safety to prevent coding errors.</li>
                                    <li><strong>Tailwind CSS</strong>: A utility-first CSS framework for rapid styling.</li>
                                    <li><strong>Prisma + PostgreSQL/Supabase</strong>: Prisma is an ORM (Object-Relational Mapping) tool for managing your database. PostgreSQL is a powerful, free database; Supabase is a user-friendly alternative.</li>
                                    <li><strong>NextAuth</strong>: Handles secure authentication (email, Google OAuth, roles).</li>
                                    <li><strong>Stripe</strong>: Manages payments and subscriptions with invoices and webhooks.</li>
                                    <li><strong>Resend</strong>: Sends transactional emails (e.g., password resets, invoices).</li>
                                    <li><strong>Docker + Coolify</strong>: Simplifies deployment on a VPS (Virtual Private Server).</li>
                                    <li><strong>Mapbox + OpenStreetMap</strong>: Displays interactive maps for listings.</li>
                                    <li><strong>FreeCurrencyAPI</strong>: Converts prices to different currencies in real-time.</li>
                                    <li><strong>Google reCAPTCHA</strong>: Protects forms from bots.</li>
                                    <li><strong>Cron Jobs</strong>: Automates tasks like currency updates or data cleanup.</li>
                                    <li><strong>Programmatic SEO</strong>: Generates sitemap.xml, robots.txt, and dynamic pages for SEO.</li>
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Section 3: Getting Started */}
                    <div id="getting-started" className="mb-10 border-b border-gray-700 pb-6">
                        <h3
                            className="text-xl font-bold mb-4 cursor-pointer text-cyan-400 hover:text-cyan-300 flex items-center"
                            onClick={() => toggleSection('getting-started')}
                        >
                            3. 🚀 Getting Started
                            <span className="ml-2">{openSections['getting-started'] !== false ? '▲' : '▼'}</span>
                        </h3>
                        {openSections['getting-started'] !== false && (
                            <div className="pl-4 text-gray-200 space-y-4">
                                <div id="installation" className="mb-6">
                                    <h4 className="text-lg font-semibold mb-2 cursor-pointer text-gray-200" onClick={() => toggleSection('installation')}>
                                        3.1 📁 Installation {openSections['installation'] ? '▲' : '▼'}
                                    </h4>
                                    {openSections['installation'] !== false && (
                                        <div className="pl-4 text-gray-200 space-y-4">
                                            <ol className="list-decimal pl-6 space-y-4">
                                                <li><strong>Download and Extract</strong>:
                                                    <ul className="list-disc pl-6 space-y-2">
                                                        <li>Unzip the <code className="bg-gray-700 px-1 rounded">peerquik-boilerplate.zip</code> file you received.</li>
                                                        <li>Navigate to the project folder:
                                                            <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                                <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">cd peerquik</pre>
                                                            </div>
                                                        </li>
                                                    </ul>
                                                </li>
                                                <li><strong>Set Up Environment Variables</strong>:
                                                    <ul className="list-disc pl-6 space-y-2">
                                                        <li>Copy the example environment file:
                                                            <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                                <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">cp .env.example .env</pre>
                                                            </div>
                                                        </li>
                                                        <li>Open <code className="bg-gray-700 px-1 rounded">.env.example</code> and create a <code className="bg-gray-700 px-1 rounded">.env</code> file, paste the variables, and add your API keys (see Section 8 for details). Example:

                                                            <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                                <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">
                                                                    {`
DATABASE_URL="postgresql://user:password@localhost:5432/peerquik"
GOOGLE_CLIENT_ID="your_google_client_id"
GOOGLE_CLIENT_SECRET="your_google_client_secret"
STRIPE_SECRET_KEY="your_stripe_secret_key"
RESEND_API_KEY="your_resend_api_key"
NEXT_PUBLIC_MAPBOX_TOKEN="your_mapbox_token"
FREECURRENCYAPI_KEY="your_currency_api_key"
RECAPTCHA_SITE_KEY="your_recaptcha_site_key"
RECAPTCHA_SECRET_KEY="your_recaptcha_secret_key"
    `}
                                                                </pre>
                                                            </div>
                                                        </li>
                                                    </ul>
                                                </li>
                                                <li><strong>Install Dependencies</strong>:
                                                    <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                        <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">npm install</pre></div>
                                                </li>
                                                <li><strong>Set Up Database</strong>:
                                                    <ul className="list-disc pl-6 space-y-2">
                                                        <li><strong>Local PostgreSQL</strong>:
                                                            <ul className="list-circle pl-6 space-y-1">
                                                                <li>Install PostgreSQL (<Link href="https://www.postgresql.org/download/" className="text-cyan-400 hover:text-cyan-300">instructions</Link>).</li>
                                                                <li>Create a database named <code className="bg-gray-700 px-1 rounded">peerquik</code>:
                                                                    <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                                        <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">psql -U postgres -c "CREATE DATABASE peerquik;"</pre></div>
                                                                </li>
                                                                <li>Update <code className="bg-gray-700 px-1 rounded">DATABASE_URL</code> in <code className="bg-gray-700 px-1 rounded">.env</code> with your credentials.</li>
                                                            </ul>
                                                        </li>
                                                        <li><strong>Supabase</strong>:
                                                            <ul className="list-circle pl-6 space-y-1">
                                                                <li>Sign up at <Link href="https://supabase.com" className="text-cyan-400 hover:text-cyan-300">supabase.com</Link>.</li>
                                                                <li>Create a new project and copy the database URL to <code className="bg-gray-700 px-1 rounded">DATABASE_URL</code> in <code className="bg-gray-700 px-1 rounded">.env</code>.</li>
                                                            </ul>
                                                        </li>
                                                        <li>Apply the database schema:
                                                            <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                                <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">npx prisma migrate dev --name init</pre></div>
                                                        </li>
                                                    </ul>
                                                </li>
                                                <li><strong>Run the App</strong>:
                                                    <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                        <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">npm run dev</pre></div>
                                                    <ul className="list-disc pl-6 space-y-2">
                                                        <li>Open <Link href="http://localhost:3000" className="text-cyan-400 hover:text-cyan-300">http://localhost:3000</Link> in your browser.</li>
                                                        <li>✅ <strong>Checkpoint</strong>: You should see the PeerQuik landing page.</li>
                                                    </ul>
                                                </li>
                                            </ol>
                                            <p className="mt-4"><strong>Note</strong>: To reuse PeerQuik for another project, copy the <code className="bg-gray-700 px-1 rounded">peerquik</code> folder to a new location and repeat these steps with new API keys and a fresh database.</p>
                                        </div>
                                    )}
                                </div>

                                <div id="customize-brand" className="mb-6">
                                    <h4 className="text-lg font-semibold mb-2 cursor-pointer text-gray-200" onClick={() => toggleSection('customize-brand')}>
                                        3.2 🎨 Customize Your Brand {openSections['customize-brand'] ? '▲' : '▼'}
                                    </h4>
                                    {openSections['customize-brand'] !== false && (
                                        <div className="pl-4 text-gray-200 space-y-4">
                                            <ol className="list-decimal pl-6 space-y-4">
                                                <li><strong>Logo and Favicon</strong>:
                                                    <ul className="list-disc pl-6 space-y-2">
                                                        <li>Replace <code className="bg-gray-700 px-1 rounded">public/logo.svg</code> with your logo.</li>
                                                        <li>Update <code className="bg-gray-700 px-1 rounded">public/favicon.ico</code> with your favicon.</li>
                                                    </ul>
                                                </li>
                                                <li><strong>Colors and Fonts</strong>:
                                                    <ul className="list-disc pl-6 space-y-2">
                                                        <li>Edit <code className="bg-gray-700 px-1 rounded">tailwind.config.ts</code>:
                                                            <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                                <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">{`
theme: {
    extend: {
        colors: {
            primary: "#123456", // Your brand color
        },
    },
}
                              `}</pre></div>
                                                        </li>
                                                        <li>Modify global styles in <code className="bg-gray-700 px-1 rounded">app/globals.css</code>.</li>
                                                    </ul>
                                                </li>
                                                <li><strong>Landing Page</strong>:
                                                    <ul className="list-disc pl-6 space-y-2">
                                                        <li>Update text and images in <code className="bg-gray-700 px-1 rounded">app/page.tsx</code>.</li>
                                                        <li>Edit site metadata in <code className="bg-gray-700 px-1 rounded">app/layout.tsx</code> and <code className="bg-gray-700 px-1 rounded">app/_document.tsx</code>.</li>
                                                    </ul>
                                                </li>
                                            </ol>
                                        </div>
                                    )}
                                </div>

                                <div id="protected-routes" className="mb-6">
                                    <h4 className="text-lg font-semibold mb-2 cursor-pointer text-gray-200" onClick={() => toggleSection('protected-routes')}>
                                        3.3 🔐 Protected Routes {openSections['protected-routes'] ? '▲' : '▼'}
                                    </h4>
                                    {openSections['protected-routes'] !== false && (
                                        <div className="pl-4 text-gray-200 space-y-4">
                                            <p className="mb-4">Routes are organized by user roles:</p>
                                            <ul className="list-disc pl-6 space-y-2">
                                                <li><code className="bg-gray-700 px-1 rounded">app/(protected)/buyer/</code>: For buyers.</li>
                                                <li><code className="bg-gray-700 px-1 rounded">app/(protected)/seller/</code>: For sellers.</li>
                                                <li><code className="bg-gray-700 px-1 rounded">app/(protected)/admin/</code>: For admins.</li>
                                            </ul>
                                            <p className="mt-4"><strong>Server-Side Protection</strong>:</p>
                                            <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">{` 
import { getServerSession } from "next-auth";
import { redirect } from "next/navigation";

const session = await getServerSession(authOptions);
if (!session) redirect("/login");
                    `}

                                                </pre></div>
                                            <p className="mt-4"><strong>Client-Side Protection</strong>:</p>
                                            <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">{`
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";

const { status } = useSession();
const router = useRouter();
if (status === "unauthenticated") router.push("/login");                                            
                    `}
                                                </pre></div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Section 4: Authentication System */}
                    <div id="authentication" className="mb-10 border-b border-gray-700 pb-6">
                        <h3
                            className="text-xl font-bold mb-4 cursor-pointer text-cyan-400 hover:text-cyan-300 flex items-center"
                            onClick={() => toggleSection('authentication')}
                        >
                            4. 🔐 Authentication System
                            <span className="ml-2">{openSections['authentication'] !== false ? '▲' : '▼'}</span>
                        </h3>
                        {openSections['authentication'] !== false && (
                            <div className="pl-4 text-gray-200 space-y-4">
                                <p className="mb-4">PeerQuik uses <strong>NextAuth.js</strong> for secure authentication:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><strong>Email Login</strong>: Supports email + password with verification (magic link).</li>
                                    <li><strong>Google OAuth</strong>: Login with Google accounts.</li>
                                    <li><strong>Roles</strong>: Buyer, Seller, Admin.</li>
                                </ul>

                                <div id="google-oauth" className="mb-6">
                                    <h4 className="text-lg font-semibold mb-2 cursor-pointer text-gray-200" onClick={() => toggleSection('google-oauth')}>
                                        4.1 Configure Google OAuth {openSections['google-oauth'] ? '▲' : '▼'}
                                    </h4>
                                    {openSections['google-oauth'] !== false && (
                                        <div className="pl-4 text-gray-200 space-y-4">
                                            <ol className="list-decimal pl-6 space-y-4">
                                                <li>Go to <Link href="https://console.cloud.google.com" className="text-cyan-400 hover:text-cyan-300">Google Cloud Console</Link>.</li>
                                                <li>Create a new project.</li>
                                                <li>Enable OAuth 2.0 and set:
                                                    <ul className="list-disc pl-6 space-y-2">
                                                        <li>Local URI: <code className="bg-gray-700 px-1 rounded">http://localhost:3000/api/auth/callback/google</code></li>
                                                        <li>Production URI: <code className="bg-gray-700 px-1 rounded">https://yourdomain.com/api/auth/callback/google</code></li>
                                                    </ul>
                                                </li>
                                                <li>Add to <code className="bg-gray-700 px-1 rounded">.env</code>:
                                                    <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                        <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">
                                                            GOOGLE_CLIENT_ID=your_id
                                                            GOOGLE_CLIENT_SECRET=your_secret
                                                        </pre></div>
                                                </li>
                                            </ol>
                                        </div>
                                    )}
                                </div>

                                <div id="roles-users" className="mb-6">
                                    <h4
                                        className="text-lg font-semibold mb-2 cursor-pointer text-gray-200"
                                        onClick={() => toggleSection('roles-users')}
                                    >
                                        4.2 Roles and User Types {openSections['roles-users'] ? '▲' : '▼'}
                                    </h4>
                                    {openSections['roles-users'] !== false && (
                                        <div className="pl-4 text-gray-200 space-y-4">
                                            <p className="mb-4">
                                                There are two user types configured in the app:
                                            </p>
                                            <ul className="list-disc list-inside space-y-2">
                                                <li>
                                                    <strong>User Type A</strong> — e.g., buyer, student, client.
                                                </li>
                                                <li>
                                                    <strong>User Type B</strong> — e.g., seller, instructor, provider.
                                                </li>
                                            </ul>
                                            <p>
                                                Each user type has its own registration and login flows, as well as
                                                personalized views, routing, and dashboard components.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div id="extend-user" className="mb-6">
                                    <h4 className="text-lg font-semibold mb-2 cursor-pointer text-gray-200" onClick={() => toggleSection('extend-user')}>
                                        4.3 Extend User Model {openSections['extend-user'] ? '▲' : '▼'}
                                    </h4>
                                    {openSections['extend-user'] !== false && (
                                        <div className="pl-4 text-gray-200 space-y-4">

                                            <p className="mb-4">To add fields to the <code className="bg-gray-700 px-1 rounded">User</code> model, edit <code className="bg-gray-700 px-1 rounded">prisma/schema.prisma</code>:</p>
                                            <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">{`
model User {
    id              String  @id @default(cuid())
    email           String  @unique
    role            String
    phone           String?
    profileComplete Boolean @default(false)
    }                                           
                                                `}
                                                </pre></div>
                                            <p className="mt-4">You can also add more models and relations. Apply changes:</p>
                                            <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">npx prisma migrate dev --name extend-user</pre></div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Section 5: Database and ORM */}
                    <div id="database-orm" className="mb-10 border-b border-gray-700 pb-6">
                        <h3
                            className="text-xl font-bold mb-4 cursor-pointer text-cyan-400 hover:text-cyan-300 flex items-center"
                            onClick={() => toggleSection('database-orm')}
                        >
                            5. 📦 Database and ORM
                            <span className="ml-2">{openSections['database-orm'] !== false ? '▲' : '▼'}</span>
                        </h3>
                        {openSections['database-orm'] !== false && (
                            <div className="pl-4 text-gray-200 space-y-4">
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><strong>Database</strong>: PostgreSQL (local or Supabase).</li>
                                    <li><strong>ORM</strong>: Prisma.</li>
                                    <li><strong>Schema</strong>: Defined in <code className="bg-gray-700 px-1 rounded">prisma/schema.prisma</code>.</li>
                                </ul>
                                <p className="mt-4">To modify the schema:</p>
                                <ol className="list-decimal pl-6 space-y-4">
                                    <li>Edit <code className="bg-gray-700 px-1 rounded">prisma/schema.prisma</code>.</li>
                                    <li>Run:
                                        <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                            <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">npx prisma migrate dev --name your_change_name</pre></div>
                                    </li>
                                </ol>
                            </div>
                        )}
                    </div>

                    {/* Section 6: Folder Structure */}
                    <div id="folder-structure" className="mb-10 border-b border-gray-700 pb-6">
                        <h3
                            className="text-xl font-bold mb-4 cursor-pointer text-cyan-400 hover:text-cyan-300 flex items-center"
                            onClick={() => toggleSection('folder-structure')}
                        >
                            6. ⚙️ Folder Structure
                            <span className="ml-2">{openSections['folder-structure'] !== false ? '▲' : '▼'}</span>
                        </h3>
                        {openSections['folder-structure'] !== false && (
                            <div className="pl-4 text-gray-200 space-y-4">
                                <p className="mb-4">PeerQuik uses Next.js App Router:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><code className="bg-gray-700 px-1 rounded">app/</code>: Routes and pages.</li>
                                    <li><code className="bg-gray-700 px-1 rounded">components/</code>: Reusable UI components (modals, hooks, maps, favorites, buttons, etc.).</li>
                                    <li><code className="bg-gray-700 px-1 rounded">actions/</code>: Server-side logic (Server Actions).</li>
                                    <li><code className="bg-gray-700 px-1 rounded">lib/</code>: Helper functions (auth, utilities).</li>
                                    <li><code className="bg-gray-700 px-1 rounded">utils/</code>: Specific utilities (e.g., currency conversion).</li>
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Section 7: Listings and Reviews */}
                    <div id="listings-reviews" className="mb-10 border-b border-gray-700 pb-6">
                        <h3
                            className="text-xl font-bold mb-4 cursor-pointer text-cyan-400 hover:text-cyan-300 flex items-center"
                            onClick={() => toggleSection('listings-reviews')}
                        >
                            7. ✍️ Listings and Reviews
                            <span className="ml-2">{openSections['listings-reviews'] !== false ? '▲' : '▼'}</span>
                        </h3>
                        {openSections['listings-reviews'] !== false && (
                            <div className="pl-4 text-gray-200 space-y-4">
                                <p className="mb-4"><strong>Flow</strong>:</p>
                                <ol className="list-decimal pl-6 space-y-4">
                                    <li>Sellers create listings.</li>
                                    <li>Buyers search and contact sellers.</li>
                                    <li>Buyers leave reviews after interactions.</li>
                                    <li>Users can mark favorite listings or sellers.</li>
                                </ol>
                                <p className="mt-4"><strong>Form Setup</strong>:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>Forms use <code className="bg-gray-700 px-1 rounded">react-hook-form</code> and <code className="bg-gray-700 px-1 rounded">zod</code> for validation.</li>
                                    <li>Custom inputs are in <code className="bg-gray-700 px-1 rounded">components/ui</code>.</li>
                                    <li>Example form:
                                        <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                            <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">{`
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

const form = useForm({ resolver: zodResolver(yourSchema) });
<Form {...form.register("field")} />
                                            `}
                                            </pre></div>
                                    </li>
                                </ul>
                                <p className="mt-4"><strong>Add Relationships</strong>:</p>
                                <ol className="list-decimal pl-6 space-y-4">
                                    <li>Update <code className="bg-gray-700 px-1 rounded">prisma/schema.prisma</code> with new models.</li>
                                    <li>Run:
                                        <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                            <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">npx prisma db push</pre></div>
                                    </li>
                                    <li>Create API routes in <code className="bg-gray-700 px-1 rounded">app/api/route.ts</code>.</li>
                                    <li>Add actions in <code className="bg-gray-700 px-1 rounded">app/actions.ts</code>.</li>
                                </ol>
                                <p className="mt-4"><strong>Frontend Customization</strong>:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>Pages are created, but you need to finalize the frontend using AI tools (e.g., for styling or layout).</li>
                                    <li>Customize the <code className="bg-gray-700 px-1 rounded">ItemCard</code> component in <code className="bg-gray-700 px-1 rounded">components/ItemCard.tsx</code> for listings or sellers using AI to match your design.</li>
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Section 8: Integrations */}
                    <div id="integrations" className="mb-10 border-b border-gray-700 pb-6">
                        <h3
                            className="text-xl font-bold mb-4 cursor-pointer text-cyan-400 hover:text-cyan-300 flex items-center"
                            onClick={() => toggleSection('integrations')}
                        >
                            8. 🧩 Integrations
                            <span className="ml-2">{openSections['integrations'] !== false ? '▲' : '▼'}</span>
                        </h3>
                        {openSections['integrations'] !== false && (
                            <div className="pl-4 text-gray-200 space-y-4">
                                <div id="stripe" className="mb-6">
                                    <h4 className="text-lg font-semibold mb-2 cursor-pointer text-gray-200" onClick={() => toggleSection('stripe')}>
                                        8.1 💳 Stripe (Subscriptions) {openSections['stripe'] ? '▲' : '▼'}
                                    </h4>
                                    {openSections['stripe'] !== false && (
                                        <div className="pl-4 text-gray-200 space-y-4">
                                            <p className="mb-4"><strong>What is it?</strong> Stripe allows you to accept payments and manage subscriptions easily.</p>
                                            <p className="mb-4"><strong>Steps</strong>:</p>
                                            <ol className="list-decimal pl-6 space-y-4">
                                                <li>Create an account at <Link href="https://stripe.com" className="text-cyan-400 hover:text-cyan-300">stripe.com</Link>.</li>
                                                <li>In the Stripe Dashboard:
                                                    <ul className="list-disc pl-6 space-y-2">
                                                        <li>Go to "Products" → Create a product for your subscription.</li>
                                                        <li>Add one or more prices (e.g., monthly, annual).</li>
                                                    </ul>
                                                </li>
                                                <li>Add to <code className="bg-gray-700 px-1 rounded">.env</code>:
                                                    <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                        <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">{`
STRIPE_SECRET_KEY=your_secret_key
STRIPE_WEBHOOK_SECRET=your_webhook_secret
NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY=your_publishable_key                                                    
                                                        `}
                                                        </pre></div>
                                                    <p className="mt-2"><strong>Note</strong>: Find these keys in Stripe Dashboard → "Developers" → "API Keys" and "Webhooks".</p>
                                                </li>
                                                <li><strong>Webhooks</strong>:
                                                    <ul className="list-disc pl-6 space-y-2">
                                                        <li>Create an endpoint in Stripe for notifications.</li>
                                                        <li>URL: <code className="bg-gray-700 px-1 rounded">https://yourapp.com/api/stripe/webhooks</code></li>
                                                        <li>Code is in: <code className="bg-gray-700 px-1 rounded">app/api/stripe/webhooks/route.ts</code></li>
                                                    </ul>
                                                </li>
                                                <li><strong>Subscriptions</strong>:
                                                    <ul className="list-disc pl-6 space-y-2">
                                                        <li>When a user subscribes, call the endpoint: <code className="bg-gray-700 px-1 rounded">app/api/stripe/subscribe-seller/route.ts</code></li>
                                                    </ul>
                                                </li>
                                                <li><strong>Invoicing</strong>:
                                                    <ul className="list-disc pl-6 space-y-2">
                                                        <li>Invoices are generated automatically in PDF using <code className="bg-gray-700 px-1 rounded">jspdf</code>.</li>
                                                        <li>Sent to users via Resend (see below).</li>
                                                    </ul>
                                                </li>
                                            </ol>
                                        </div>
                                    )}
                                </div>

                                <div id="resend" className="mb-6">
                                    <h4 className="text-lg font-semibold mb-2 cursor-pointer text-gray-200" onClick={() => toggleSection('resend')}>
                                        8.2 📩 Emails with Resend {openSections['resend'] ? '▲' : '▼'}
                                    </h4>
                                    {openSections['resend'] !== false && (
                                        <div className="pl-4 text-gray-200 space-y-4">
                                            <p className="mb-4"><strong>What is it?</strong> A service for sending emails from your app without complications.</p>
                                            <p className="mb-4"><strong>Steps</strong>:</p>
                                            <ol className="list-decimal pl-6 space-y-4">
                                                <li>Create an account at <Link href="https://resend.com" className="text-cyan-400 hover:text-cyan-300">resend.com</Link>.</li>
                                                <li><strong>Important</strong>: If you don’t verify your domain, you can only send emails from the registered email.</li>
                                                <li>Add to <code className="bg-gray-700 px-1 rounded">.env</code>:
                                                    <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                        <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">RESEND_API_KEY=your_resend_key</pre></div>
                                                </li>
                                                <li>Edit <code className="bg-gray-700 px-1 rounded">actions/mail.ts</code> to set the sender:
                                                    <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                        <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">
                                                            from: "Your App {'<noreply@yourapp.com>'}",
                                                        </pre></div>
                                                    <p className="mt-2">Change <code className="bg-gray-700 px-1 rounded">yourapp.com</code> to your domain or use the registered email if unverified.</p>
                                                </li>
                                                <li>Test email templates locally:
                                                    <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                        <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">npm run email</pre></div>
                                                </li>
                                            </ol>
                                        </div>
                                    )}
                                </div>

                                <div id="maps" className="mb-6">
                                    <h4 className="text-lg font-semibold mb-2 cursor-pointer text-gray-200" onClick={() => toggleSection('maps')}>
                                        8.3 🗺 Maps (Mapbox + OpenStreetMap) {openSections['maps'] ? '▲' : '▼'}
                                    </h4>
                                    {openSections['maps'] !== false && (
                                        <div className="pl-4 text-gray-200 space-y-4">
                                            <p className="mb-4"><strong>What is it?</strong> Displays maps for user locations, centers, or searches.</p>
                                            <p className="mb-4"><strong>Steps</strong>:</p>
                                            <ol className="list-decimal pl-6 space-y-4">
                                                <li>Create an account at <Link href="https://mapbox.com" className="text-cyan-400 hover:text-cyan-300">mapbox.com</Link>.</li>
                                                <li>Copy your access token and add to <code className="bg-gray-700 px-1 rounded">.env</code>:
                                                    <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                        <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">NEXT_PUBLIC_MAPBOX_TOKEN=your_mapbox_token</pre></div>
                                                </li>
                                            </ol>
                                            <p className="mt-4">Mapbox + OpenStreetMap powers maps in listings and search.</p>
                                        </div>
                                    )}
                                </div>

                                <div id="currency" className="mb-6">
                                    <h4 className="text-lg font-semibold mb-2 cursor-pointer text-gray-200" onClick={() => toggleSection('currency')}>
                                        8.4 💱 FreeCurrencyAPI {openSections['currency'] ? '▲' : '▼'}
                                    </h4>
                                    {openSections['currency'] !== false && (
                                        <div className="pl-4 text-gray-200 space-y-4">
                                            <p className="mb-4"><strong>What is it?</strong> Converts prices between different currencies.</p>
                                            <p className="mb-4"><strong>Steps</strong>:</p>
                                            <ol className="list-decimal pl-6 space-y-4">
                                                <li>Create an account at <Link href="https://freecurrencyapi.com" className="text-cyan-400 hover:text-cyan-300">freecurrencyapi.com</Link>.</li>
                                                <li>Add to <code className="bg-gray-700 px-1 rounded">.env</code>:
                                                    <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                        <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">FREECURRENCYAPI_KEY=your_api_key</pre></div>
                                                </li>
                                                <li>Conversion logic is in: <code className="bg-gray-700 px-1 rounded">utils/currencyconverter.ts</code>.</li>
                                                <li>A cron job updates exchange rates periodically (see Section 9).</li>
                                            </ol>
                                        </div>
                                    )}
                                </div>

                                <div id="recaptcha" className="mb-6">
                                    <h4 className="text-lg font-semibold mb-2 cursor-pointer text-gray-200" onClick={() => toggleSection('recaptcha')}>
                                        8.5 ✅ Google reCAPTCHA {openSections['recaptcha'] ? '▲' : '▼'}
                                    </h4>
                                    {openSections['recaptcha'] !== false && (
                                        <div className="pl-4 text-gray-200 space-y-4">
                                            <p className="mb-4"><strong>What is it?</strong> Prevents spam in forms, especially for non-logged-in users.</p>
                                            <p className="mb-4"><strong>Steps</strong>:</p>
                                            <ol className="list-decimal pl-6 space-y-4">
                                                <li>Go to <Link href="https://www.google.com/recaptcha/admin" className="text-cyan-400 hover:text-cyan-300">Google reCAPTCHA Admin</Link>.</li>
                                                <li>Register your domain (e.g., <code className="bg-gray-700 px-1 rounded">yourapp.com</code>).</li>
                                                <li>Add keys to <code className="bg-gray-700 px-1 rounded">.env</code>:
                                                    <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                        <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">{`
RECAPTCHA_SITE_KEY=your_site_key
RECAPTCHA_SECRET_KEY=your_secret_key                                                   
                                                        `}
                                                        </pre></div>
                                                </li>
                                            </ol>
                                            <p className="mt-4">Used in public forms (registration, contact, etc.).</p>
                                        </div>
                                    )}
                                </div>

                                <div id="newsletter" className="mb-6">
                                    <h4 className="text-lg font-semibold mb-2 cursor-pointer text-gray-200" onClick={() => toggleSection('newsletter')}>
                                        8.6 📬 Newsletter Footer {openSections['newsletter'] ? '▲' : '▼'}
                                    </h4>
                                    {openSections['newsletter'] !== false && (
                                        <div className="pl-4 text-gray-200 space-y-4">
                                            <ol className="list-decimal pl-6 space-y-4">
                                                <li>Use Resend to manage newsletter subscriptions.</li>
                                                <li>Edit the footer component in <code className="bg-gray-700 px-1 rounded">components/footer.tsx</code>.</li>
                                                <li>Add a form to collect emails and save to the <code className="bg-gray-700 px-1 rounded">Newsletter</code> database model.</li>
                                            </ol>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Section 9: Cron Jobs */}
                    <div id="cron-jobs" className="mb-10 border-b border-gray-700 pb-6">
                        <h3
                            className="text-xl font-bold mb-4 cursor-pointer text-cyan-400 hover:text-cyan-300 flex items-center"
                            onClick={() => toggleSection('cron-jobs')}
                        >
                            9. ⏰ Cron Jobs
                            <span className="ml-2">{openSections['cron-jobs'] !== false ? '▲' : '▼'}</span>
                        </h3>
                        {openSections['cron-jobs'] !== false && (
                            <div className="pl-4 text-gray-200 space-y-4">
                                <p className="mb-4">Cron jobs automate tasks like currency updates or data cleanup.</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><strong>Scripts</strong>: Defined in <code className="bg-gray-700 px-1 rounded">utils/cronJob.ts</code>. Add all cron jobs here.</li>
                                    <li><strong>Vercel Limitation</strong>: The free tier allows only one cron job. For more, use a VPS with Coolify.</li>
                                    <li><strong>Coolify</strong>:
                                        <ul className="list-circle pl-6 space-y-1">
                                            <li>Go to the "Cron Jobs" panel in Coolify.</li>
                                            <li>Watch my tutorial on <Link href="https://www.youtube.com/watch?v=KVBIVma0MeU" className="text-cyan-400 hover:text-cyan-300" target="_blank" rel="noopener noreferrer">YouTube</Link> if you have questions.</li>
                                        </ul>
                                    </li>
                                    <li><strong>Vercel</strong>:
                                        <ul className="list-circle pl-6 space-y-1">
                                            <li>Edit <code className="bg-gray-700 px-1 rounded">vercel-cron.json</code> to configure cron jobs.</li>
                                        </ul>
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Section 10: SEO & Programmatic SEO */}
                    <div id="seo" className="mb-10 border-b border-gray-700 pb-6">
                        <h3
                            className="text-xl font-bold mb-4 cursor-pointer text-cyan-400 hover:text-cyan-300 flex items-center"
                            onClick={() => toggleSection('seo')}
                        >
                            10. 🧠 SEO & Programmatic SEO
                            <span className="ml-2">{openSections['seo'] !== false ? '▲' : '▼'}</span>
                        </h3>
                        {openSections['seo'] !== false && (
                            <div className="pl-4 text-gray-200 space-y-4">
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><strong>Sitemap</strong>: Auto-generated in <code className="bg-gray-700 px-1 rounded">app/sitemap.ts</code>.</li>
                                    <li><strong>Robots.txt</strong>: Configured in <code className="bg-gray-700 px-1 rounded">app/robots.ts</code>.</li>
                                    <li><strong>PSEO</strong>: The <code className="bg-gray-700 px-1 rounded">/pseo</code> folder generates dynamic pages for keywords or regions.
                                        {/* <ul className="list-circle pl-6 space-y-1">
                      <li>Example: Create a page for each city:
                              <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                                <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">
                          // app/pseo/[city]/page.tsx
                          export async function generateStaticParams() {'{'}
                            const cities = ["new-york", "london"];
                            return cities.map((city) => ({'{'} city {'}'}));
                          {'}'}
                        </code></pre>
                      </li>
                    </ul> */}
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Section 11: ChatGPT Prompts */}
                    <div id="chatgpt-prompts" className="mb-10 border-b border-gray-700 pb-6">
                        <h3
                            className="text-xl font-bold mb-4 cursor-pointer text-cyan-400 hover:text-cyan-300 flex items-center"
                            onClick={() => toggleSection('chatgpt-prompts')}
                        >
                            11. 📜 ChatGPT Prompts
                            <span className="ml-2">{openSections['chatgpt-prompts'] !== false ? '▲' : '▼'}</span>
                        </h3>
                        {openSections['chatgpt-prompts'] !== false && (
                            <div className="pl-4 text-gray-200 space-y-4">
                                <p className="mb-4">Use the provided prompts to generate:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><strong>Terms of Service</strong>: Save in <code className="bg-gray-700 px-1 rounded">app/terms-of-service/page.tsx</code>.</li>
                                    <li><strong>Privacy Policy</strong>: Save in <code className="bg-gray-700 px-1 rounded">app/privacy-policy/page.tsx</code>.</li>
                                    <li><strong>User Data Deletion</strong>: Save in <code className="bg-gray-700 px-1 rounded">app/user-data-deletion/page.tsx</code>.</li>
                                </ul>
                                <p className="mt-4">Run the prompts in ChatGPT or a similar AI tool and paste the output.</p>
                            </div>
                        )}
                    </div>

                    {/* Section 12: Deployment */}
                    <div id="deployment" className="mb-10 border-b border-gray-700 pb-6">
                        <h3
                            className="text-xl font-bold mb-4 cursor-pointer text-cyan-400 hover:text-cyan-300 flex items-center"
                            onClick={() => toggleSection('deployment')}
                        >
                            12. 📦 Deployment
                            <span className="ml-2">{openSections['deployment'] !== false ? '▲' : '▼'}</span>
                        </h3>
                        {openSections['deployment'] !== false && (
                            <div className="pl-4 text-gray-200 space-y-4">
                                <div id="vercel-supabase" className="mb-6">
                                    <h4 className="text-lg font-semibold mb-2 cursor-pointer text-gray-200" onClick={() => toggleSection('vercel-supabase')}>
                                        12.1 🌩️ Vercel + Supabase (Easy) {openSections['vercel-supabase'] ? '▲' : '▼'}
                                    </h4>
                                    {openSections['vercel-supabase'] !== false && (
                                        <div className="pl-4 text-gray-200 space-y-4">
                                            <ol className="list-decimal pl-6 space-y-4">
                                                <li>Push your project to GitHub.</li>
                                                <li>Connect to <Link href="https://vercel.com" className="text-cyan-400 hover:text-cyan-300">vercel.com</Link>.</li>
                                                <li>Add <code className="bg-gray-700 px-1 rounded">.env</code> variables in Vercel Settings.</li>
                                                <li>Deploy the app.</li>
                                                <li>Set up a Supabase database (see Section 3.1).</li>
                                            </ol>
                                        </div>
                                    )}
                                </div>

                                <div id="coolify-vps" className="mb-6">
                                    <h4 className="text-lg font-semibold mb-2 cursor-pointer text-gray-200" onClick={() => toggleSection('coolify-vps')}>
                                        12.2 🐳 Coolify + VPS {openSections['coolify-vps'] ? '▲' : '▼'}
                                    </h4>
                                    {openSections['coolify-vps'] !== false && (
                                        <div className="pl-4 text-gray-200 space-y-4">
                                            <ol className="list-decimal pl-6 space-y-4">
                                                <li>Set up a VPS (e.g., DigitalOcean, Hetzner).</li>
                                                <li>Install Docker and Coolify (<Link href="https://coolify.io/docs" className="text-cyan-400 hover:text-cyan-300">docs</Link>).</li>
                                                <li>Authenticate with GitHub Container Registry:
                                                    <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                        <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">
                                                            echo "your_token" | docker login ghcr.io -u your_username --password-stdin
                                                        </pre></div>
                                                </li>
                                                <li>Build and push:
                                                    <div className="my-3 bg-gray-700 p-4 rounded text-gray-100 text-xs sm:text-base leading-relaxed overflow-x-auto">
                                                        <pre className="whitespace-pre-wrap sm:whitespace-pre font-mono">{`
docker build -t ghcr.io/your_username/peerquik:latest .
docker push ghcr.io/your_username/peerquik:latest                                                   
                                                        `}
                                                        </pre></div>
                                                </li>
                                                <li>Deploy via Coolify’s dashboard.</li>
                                                <li>Watch my tutorial on <Link href="https://www.youtube.com/watch?v=KVBIVma0MeU" className="text-cyan-400 hover:text-cyan-300" target="_blank" rel="noopener noreferrer">YouTube</Link> if you have questions.</li>
                                            </ol>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Section 13: Troubleshooting */}
                    <div id="troubleshooting" className="mb-10 border-b border-gray-700 pb-6">
                        <h3
                            className="text-xl font-bold mb-4 cursor-pointer text-cyan-400 hover:text-cyan-300 flex items-center"
                            onClick={() => toggleSection('troubleshooting')}
                        >
                            13. 🛠 Troubleshooting
                            <span className="ml-2">{openSections['troubleshooting'] !== false ? '▲' : '▼'}</span>
                        </h3>
                        {openSections['troubleshooting'] !== false && (
                            <div className="pl-4 text-gray-200 space-y-4">
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><strong>Error: Database connection failed</strong>:
                                        <ul className="list-circle pl-6 space-y-1">
                                            <li>Check <code className="bg-gray-700 px-1 rounded">DATABASE_URL</code> in <code className="bg-gray-700 px-1 rounded">.env</code>.</li>
                                            <li>Ensure PostgreSQL/Supabase is running.</li>
                                        </ul>
                                    </li>
                                    <li><strong>Error: Stripe webhook not firing</strong>:
                                        <ul className="list-circle pl-6 space-y-1">
                                            <li>Verify <code className="bg-gray-700 px-1 rounded">STRIPE_WEBHOOK_SECRET</code> in <code className="bg-gray-700 px-1 rounded">.env</code>.</li>
                                            <li>Test webhooks locally with <code className="bg-gray-700 px-1 rounded">stripe listen --forward-to localhost:3000/api/stripe/webhooks</code>.</li>
                                        </ul>
                                    </li>
                                    <li><strong>Error: Prisma migrate failed</strong>:
                                        <ul className="list-circle pl-6 space-y-1">
                                            <li>Run <code className="bg-gray-700 px-1 rounded">npx prisma generate</code> and retry.</li>
                                        </ul>
                                    </li>
                                    <li><strong>Need Help?</strong> Email support (included with your purchase) or check <code className="bg-gray-700 px-1 rounded">README.md</code>.</li>
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Section 14: Reusing PeerQuik */}
                    <div id="reusing-peerquik" className="mb-10 border-b border-gray-700 pb-6">
                        <h3
                            className="text-xl font-bold mb-4 cursor-pointer text-cyan-400 hover:text-cyan-300 flex items-center"
                            onClick={() => toggleSection('reusing-peerquik')}
                        >
                            14. ♻️ Reusing PeerQuik
                            <span className="ml-2">{openSections['reusing-peerquik'] !== false ? '▲' : '▼'}</span>
                        </h3>
                        {openSections['reusing-peerquik'] !== false && (
                            <div className="pl-4 text-gray-200 space-y-4">
                                <p className="mb-4">To create another project with PeerQuik:</p>
                                <ol className="list-decimal pl-6 space-y-4">
                                    <li>Copy the <code className="bg-gray-700 px-1 rounded">peerquik</code> folder to a new location.</li>
                                    <li>Create a new database and update <code className="bg-gray-700 px-1 rounded">DATABASE_URL</code>.</li>
                                    <li>Generate new API keys for all services.</li>
                                    <li>Repeat the installation steps (Section 3.1).</li>
                                </ol>
                                <p className="mt-4">There’s no limit to how many times you can reuse PeerQuik!</p>
                            </div>
                        )}
                    </div>

                    {/* Section 15: Features at a Glance */}
                    <div id="features" className="mb-10 border-b border-gray-700 pb-6">
                        <h3
                            className="text-xl font-bold mb-4 cursor-pointer text-cyan-400 hover:text-cyan-300 flex items-center"
                            onClick={() => toggleSection('features')}
                        >
                            15. 🎉 Features at a Glance
                            <span className="ml-2">{openSections['features'] !== false ? '▲' : '▼'}</span>
                        </h3>
                        {openSections['features'] !== false && (
                            <div className="pl-4 text-gray-200 space-y-4">
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>✅ Next.js 14 boilerplate (App Router, TypeScript)</li>
                                    <li>✅ Prisma database schema (PostgreSQL/Supabase)</li>
                                    <li>✅ Register/login for Buyers & Sellers (Email + Google OAuth)</li>
                                    <li>✅ Forgot password, change email, email verification</li>
                                    <li>✅ Contact form with Resend emails</li>
                                    <li>✅ Admin, Buyer, Seller, Listings, and Reviews routes</li>
                                    <li>✅ Admin panel for management</li>
                                    <li>✅ Footer with newsletter setup</li>
                                    <li>✅ Reviews calculations and components</li>
                                    <li>✅ Navbar and search listings</li>
                                    <li>✅ Custom hooks for reusability</li>
                                    <li>✅ Currency converter</li>
                                    <li>✅ Cron jobs for automated tasks</li>
                                    <li>✅ Forms with CAPTCHA protection</li>
                                    <li>✅ Extensive components (modals, buttons, headings, errors, maps)</li>
                                    <li>✅ Responsive frontend pages (landing, etc.)</li>
                                    <li>✅ Add favorites for Sellers/Listings</li>
                                    <li>✅ Premium subscription for Sellers (Stripe + invoices + emails)</li>
                                    <li>✅ Sitemap generator, robots.txt, PSEO folder</li>
                                    <li>✅ ChatGPT prompts for legal pages</li>
                                    <li>✅ README with setup instructions</li>
                                    <li>✅ Dockerfile, .dockerignore, docker-compose for VPS/Coolify</li>
                                </ul>
                            </div>
                        )}
                    </div>

                    <p className="mt-8 text-lg font-semibold text-white">Happy building with PeerQuik! 🚀</p>
                </div>
            </div>
        </div>
    );
}